import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  buildSubscriptionGrant,
  clientName,
  eligiblePlan,
  planEntitlements,
  planNumber,
} from "./ceoSubscriptionModel";
import "./ceoOnboarding.css";
import "./ceoSubscriptions.css";
const empty = {
  clientId: "",
  planId: "",
  type: "FREE_TRIAL",
  durationDays: "14",
  reason: "",
};
export default function CEOSubscriptions({
  loadSubscriptions,
  onGrantSubscription,
  onRevokeGrant,
}) {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    ...empty,
    clientId: params.get("clientId") || "",
  });
  const [data, setData] = useState({ clients: [], plans: [], grants: [] });
  const [loading, setLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [review, setReview] = useState(null),
    [refresh, setRefresh] = useState(0),
    [search, setSearch] = useState("");
  const [revoke, setRevoke] = useState(null),
    [revokeReason, setRevokeReason] = useState("");
  const lock = useRef(false),
    request = useRef(null);
  useEffect(() => {
    if (!loadSubscriptions) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => loadSubscriptions({ signal: controller.signal }))
      .then((result) => {
        if (controller.signal.aborted) return;
        if (
          !Array.isArray(result?.clients) ||
          !Array.isArray(result?.plans) ||
          !Array.isArray(result?.grants)
        )
          throw new Error("Subscription service returned an invalid response.");
        setData(result);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e.message || "Unable to load subscriptions.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [loadSubscriptions, refresh]);
  const update = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setReview(null);
    request.current = null;
  };
  const client = data.clients.find((c) => c.clientId === form.clientId);
  const availablePlans = data.plans.filter(eligiblePlan);
  const plan = availablePlans.find((p) => p.planId === form.planId);
  const entitlements = planEntitlements(plan);
  const prepare = (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    try {
      if (!client || !plan)
        throw new Error("Choose a client and plan from the current records.");
      setReview(
        buildSubscriptionGrant({
          ...form,
          expectedSubscriptionId: client.subscription?.subscriptionId,
        }),
      );
      request.current = window.crypto.randomUUID();
    } catch (e) {
      setError(e.message);
    }
  };
  const act = async (type) => {
    const handler = type === "grant" ? onGrantSubscription : onRevokeGrant;
    if (lock.current || !handler) return;
    setError("");
    setNotice("");
    const reason = revokeReason.trim();
    if (type === "revoke" && !reason) {
      setError("Provide a revocation reason.");
      return;
    }
    if (type === "grant" && !review) return;
    lock.current = true;
    setBusy(true);
    try {
      if (!request.current) request.current = window.crypto.randomUUID();
      const result = await handler(
        type === "grant"
          ? review
          : { grantId: revoke.grantId, clientId: revoke.clientId, reason },
        { requestId: request.current },
      );
      if (![200, 201].includes(Number(result?.status)))
        throw new Error(
          result?.message || "The server did not confirm the change.",
        );
      setNotice(result.message || "Subscription updated.");
      setReview(null);
      setRevoke(null);
      setRevokeReason("");
      setForm({ ...empty });
      request.current = null;
      setRefresh((n) => n + 1);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          "Unable to update subscription. Retry retains the same request identifier.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const filtered = data.clients.filter((c) =>
    `${clientName(c)} ${c.clientId} ${c.founder?.email || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const date = (value) =>
    value
      ? new Date(value).toLocaleString("en-NG", { timeZone: "Africa/Lagos" })
      : "—";
  return (
    <section className="ceo-surface ceo-onboarding ceo-subscriptions">
      <div className="ceo-onboarding__heading">
        <div>
          <h2>Subscription grants</h2>
          <p className="ceo-subtle">
            Give clients a free trial or a time-limited free subscription.
          </p>
        </div>
        <button
          className="ceo-button"
          disabled={loading || busy || !loadSubscriptions}
          onClick={() => setRefresh((n) => n + 1)}
        >
          Refresh
        </button>
      </div>
      {error && (
        <p role="alert" className="ceo-onboarding__error">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {loading && <p role="status">Loading client subscriptions…</p>}
      {!loadSubscriptions && (
        <p>
          Connect the subscription service to load client accounts and grant
          history.
        </p>
      )}
      <form onSubmit={prepare}>
        <fieldset disabled={busy || loading || !loadSubscriptions}>
          <legend>New grant</legend>
          <div className="ceo-onboarding__grid">
            <label className="ceo-onboarding__field">
              <span>Find client</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Business name, ID or email"
              />
            </label>
            <label className="ceo-onboarding__field">
              <span>Client *</span>
              <select
                required
                value={form.clientId}
                onChange={(e) => update("clientId", e.target.value)}
              >
                <option value="">Select client</option>
                {[
                  ...new Map(
                    [...(client ? [client] : []), ...filtered].map((c) => [
                      c.clientId,
                      c,
                    ]),
                  ).values(),
                ].map((c) => (
                  <option key={c.clientId} value={c.clientId}>
                    {clientName(c)} · {c.clientId}
                  </option>
                ))}
              </select>
            </label>
            <label className="ceo-onboarding__field">
              <span>Plan *</span>
              <select
                required
                value={form.planId}
                onChange={(e) => update("planId", e.target.value)}
              >
                <option value="">Select plan</option>
                {availablePlans.map((p) => (
                  <option key={p.planId} value={p.planId}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="ceo-onboarding__field">
              <span>Grant type *</span>
              <select
                value={form.type}
                onChange={(e) => update("type", e.target.value)}
              >
                <option value="FREE_TRIAL">Free trial</option>
                <option value="FREE_SUBSCRIPTION">Free subscription</option>
              </select>
            </label>
            <label className="ceo-onboarding__field">
              <span>Duration in days *</span>
              <input
                required
                type="number"
                min="1"
                max="3650"
                step="1"
                value={form.durationDays}
                onChange={(e) => update("durationDays", e.target.value)}
              />
            </label>
            <label className="ceo-onboarding__field">
              <span>Reason *</span>
              <input
                required
                value={form.reason}
                onChange={(e) => update("reason", e.target.value)}
              />
            </label>
          </div>
          {plan && (
            <div className="ceo-onboarding__review">
              <h3>{plan.name} entitlement</h3>
              <p>{plan.description}</p>
              <p>
                {plan.currency}{" "}
                {planNumber(plan.pricing?.monthly)?.toLocaleString() ?? "—"}{" "}
                monthly · {plan.currency}{" "}
                {planNumber(plan.pricing?.yearly)?.toLocaleString() ?? "—"}{" "}
                yearly
              </p>
              <p>
                Grant charge: {plan.currency} 0. The paid prices above are for
                reference.
              </p>
              <dl>
                {entitlements.limits.map((limit) => (
                  <div key={limit.name}>
                    <dt>{limit.name.replace(/([A-Z])/g, " $1")}</dt>
                    <dd>
                      {limit.value === -1
                        ? "Unlimited"
                        : limit.value === null
                          ? "Not specified"
                          : limit.value.toLocaleString()}
                    </dd>
                  </div>
                ))}
              </dl>
              <p>
                <strong>Included modules:</strong>{" "}
                {entitlements.modules.join(", ") || "None"}
              </p>
              <details>
                <summary>Capabilities and exclusions</summary>
                <p>
                  {entitlements.capabilities.join(", ") ||
                    "No capabilities listed"}
                </p>
                <p>
                  <strong>Unavailable:</strong>{" "}
                  {entitlements.unavailableModules.join(", ") || "None"}
                </p>
              </details>
              <p>
                <strong>Support:</strong>{" "}
                {Object.entries(plan.support || {})
                  .filter(([, enabled]) => enabled === true)
                  .map(([name]) => name)
                  .join(", ") || "None listed"}
              </p>
              <p>
                <strong>Backup:</strong>{" "}
                {plan.backup?.enabled ? "Enabled" : "Disabled"} ·{" "}
                <strong>API:</strong>{" "}
                {plan.integrations?.api ? "Enabled" : "Disabled"} ·{" "}
                <strong>Plan audit logs:</strong>{" "}
                {plan.security?.auditLogs ? "Included" : "Not included"}
              </p>
            </div>
          )}
          {client && (
            <p>
              Current subscription: {client.subscription?.planName || "None"} ·{" "}
              {client.subscription?.status || "Unassigned"} · expires{" "}
              {date(client.subscription?.expiryDate)}
            </p>
          )}
          <p className="ceo-subtle">
            The grant begins when the server approves it. Auto-renew is off.
          </p>
          <button
            className="ceo-button"
            disabled={!onGrantSubscription}
            type="submit"
          >
            Review grant
          </button>
        </fieldset>
      </form>
      {review && (
        <div className="ceo-onboarding__review">
          <h3>Confirm grant</h3>
          <p>
            {clientName(client)} receives {plan.name} for {review.durationDays}{" "}
            days as a{" "}
            {review.type === "FREE_TRIAL" ? "free trial" : "free subscription"}.
          </p>
          <p>Reason: {review.reason}</p>
          <p>
            This changes the client’s current subscription. Confirm after
            reviewing any existing paid entitlement.
          </p>
          <div className="ceo-onboarding__actions">
            <button
              className="ceo-button"
              disabled={busy}
              onClick={() => {
                setReview(null);
                request.current = null;
              }}
            >
              Cancel
            </button>
            <button
              className="ceo-button ceo-button--primary"
              disabled={busy || !onGrantSubscription}
              onClick={() => act("grant")}
            >
              {busy ? "Applying…" : "Confirm grant"}
            </button>
          </div>
        </div>
      )}
      <h3>Grant history</h3>
      <div className="ceo-subscriptions__table">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Grant / plan</th>
              <th>Status</th>
              <th>Expires (Lagos)</th>
              <th>Granted by / reason</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.grants.map((g) => (
              <tr key={g.grantId}>
                <td>{g.clientName || g.clientId}</td>
                <td>
                  {g.type} / {g.planName}
                </td>
                <td>{g.status}</td>
                <td>{date(g.expiryDate)}</td>
                <td>
                  {g.createdByName || g.createdBy || "—"}
                  <br />
                  {g.reason}
                </td>
                <td>
                  {g.status === "ACTIVE" && (
                    <button
                      className="ceo-button"
                      disabled={busy || !onRevokeGrant}
                      onClick={() => {
                        setRevoke(g);
                        setRevokeReason("");
                        setReview(null);
                        request.current = null;
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && !data.grants.length && (
        <p className="ceo-subtle">No grant history available.</p>
      )}
      {revoke && (
        <div className="ceo-onboarding__review">
          <h3>Revoke grant for {revoke.clientName || revoke.clientId}</h3>
          <label className="ceo-onboarding__field">
            <span>Reason *</span>
            <input
              disabled={busy}
              value={revokeReason}
              onChange={(e) => {
                setRevokeReason(e.target.value);
                request.current = null;
              }}
            />
          </label>
          <p>
            The server resolves the client’s remaining entitlement after
            revocation.
          </p>
          <div className="ceo-onboarding__actions">
            <button
              className="ceo-button"
              disabled={busy}
              onClick={() => {
                setRevoke(null);
                request.current = null;
              }}
            >
              Cancel
            </button>
            <button
              className="ceo-button ceo-button--primary"
              disabled={busy || !onRevokeGrant}
              onClick={() => act("revoke")}
            >
              Confirm revocation
            </button>
          </div>
        </div>
      )}
      {!onGrantSubscription && (
        <p className="ceo-subtle">
          Grant creation awaits an authorized CEO endpoint.
        </p>
      )}
    </section>
  );
}
